// src/context/AuthContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { getLoginRedirectUri, loginRequest, relayState } from "../authConfig";
import axiosClient from "../api/axiosClient";
import { refreshTokenClaims } from "../api/userApi";
import { parseRolePermissionsResponse } from "../utils/roleAccess";
import { clearSessionRefreshNotice } from "../api/sessionRefresh";

const AuthContext = createContext();
const EXPIRY_WARNING_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [noAccess, setNoAccess] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [roleCapabilities, setRoleCapabilities] = useState({
    finance_access_roles: [],
    full_property_access_roles: [],
    system_admin_roles: [],
  });
  const [initialized, setInitialized] = useState(false);
  const [sessionInfo, setSessionInfo] = useState({
    issuedAt: null,
    expiresAt: null,
    remainingMs: null,
  });
  const [sessionToast, setSessionToast] = useState(() => {
    try {
      const raw = sessionStorage.getItem("sessionToast");
      return raw
        ? JSON.parse(raw)
        : { open: false, message: "", severity: "warning" };
    } catch (e) {
      return { open: false, message: "", severity: "warning" };
    }
  });

  const warnedRef = useRef(false);
  const expiredRef = useRef(false);

  const decodeJwtPayload = (rawToken) => {
    try {
      const base64Url = rawToken.split(".")[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.warn("Failed to decode JWT payload:", e);
      return null;
    }
  };

  const formatRelativeDuration = (ms) => {
    if (ms == null) return "unknown time";
    const seconds = Math.max(0, Math.floor(ms / 1000));
    if (seconds < 60) return "less than a minute";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"}`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;
    const years = Math.floor(months / 12);
    return `${years} year${years === 1 ? "" : "s"}`;
  };

  const logoutLocal = useCallback(async (message) => {
    const account = instance.getActiveAccount();
    if (account) {
      try {
        await instance.removeAccount(account);
      } catch (e) {
        console.warn("Failed to remove account:", e);
      }
    }
    setUser(null);
    setToken(null);
    setNoAccess(false);
    setPermissions({});
    setRoleCapabilities({
      finance_access_roles: [],
      full_property_access_roles: [],
      system_admin_roles: [],
    });
    if (message) {
      setSessionToast({
        open: true,
        message,
        severity: "info",
      });
    }
  }, [instance]);

  const logoutWithB2C = useCallback(async (message) => {
    const postLogoutRedirectUri = getLoginRedirectUri();
    try {
      await instance.logoutPopup({ postLogoutRedirectUri, state: relayState() });
    } catch (e) {
      console.warn("Logout popup failed:", e);
    } finally {
      await logoutLocal(message);
    }
  }, [instance, logoutLocal]);

  const loadUser = useCallback(async () => {
    if (inProgress !== "none") return;

    try {
      let active = instance.getActiveAccount() || accounts[0];

      if (!active) {
        setNoAccess(false);
        setInitialized(true);
        return;
      }

      instance.setActiveAccount(active);

      // Always acquire token silently after redirect
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: active,
      });

      const accessToken = response.accessToken;

      // Load  user profile
      const me = await axiosClient.get("/auth/me/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // load role permissions
      const perms = await axiosClient.get("/auth/role-permissions/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Merge MSAL account + profile details
      // me.data contains { role (from claims), roles (array from claims), claims_role, claims_prop, claims_units }
      // All role information comes from JWT claims, not database
      const claimsRole = Array.isArray(me.data?.claims_role) ? me.data.claims_role : [];
      const claimsProp = Array.isArray(me.data?.claims_prop) ? me.data.claims_prop : [];
      const claimsUnits = Array.isArray(me.data?.claims_units) ? me.data.claims_units : [];
      const hasClaimsAccess =
        claimsRole.length > 0 || claimsProp.length > 0 || claimsUnits.length > 0;

      setUser({
        ...active,
        ...me.data,
      });

      setNoAccess(!hasClaimsAccess);
      const parsedPermissions = parseRolePermissionsResponse(perms.data);
      setPermissions(parsedPermissions.permissions);
      setRoleCapabilities(parsedPermissions.roleCapabilities);
      setToken(accessToken);
      setSessionToast((prev) => (prev.open ? { ...prev, open: false, message: "" } : prev));
      clearSessionRefreshNotice();

    } catch (e) {
      console.error("AuthContext error:", e);
    } finally {
      setInitialized(true);
    }
  }, [accounts, inProgress, instance]);

  const refreshToken = async () => {
    try {
      // Check if token needs refresh
      const checkResult = await refreshTokenClaims();
      
      if (checkResult.needs_refresh) {
        // Force interactive login to get new token with updated claims
        const response = await instance.acquireTokenPopup({
          ...loginRequest,
        });
        
        const accessToken = response.accessToken;
        
        // Reload user profile with new token
        const me = await axiosClient.get("/auth/me/", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const perms = await axiosClient.get("/auth/role-permissions/", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        setUser({
          ...instance.getActiveAccount(),
          ...me.data,
        });

        const claimsRole = Array.isArray(me.data?.claims_role) ? me.data.claims_role : [];
        const claimsProp = Array.isArray(me.data?.claims_prop) ? me.data.claims_prop : [];
        const claimsUnits = Array.isArray(me.data?.claims_units) ? me.data.claims_units : [];
        const hasClaimsAccess =
          claimsRole.length > 0 || claimsProp.length > 0 || claimsUnits.length > 0;
        setNoAccess(!hasClaimsAccess);
        const parsedPermissions = parseRolePermissionsResponse(perms.data);
        setPermissions(parsedPermissions.permissions);
        setRoleCapabilities(parsedPermissions.roleCapabilities);
        setToken(accessToken);
        setSessionToast((prev) => (prev.open ? { ...prev, open: false, message: "" } : prev));
        clearSessionRefreshNotice();

        return true;
      }
      return false;
    } catch (e) {
      console.error("Token refresh error:", e);
      throw e;
    }
  };

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (!token) {
      setSessionInfo({ issuedAt: null, expiresAt: null, remainingMs: null });
      warnedRef.current = false;
      expiredRef.current = false;
      return;
    }

    const payload = decodeJwtPayload(token);
    const issuedAt = payload?.iat ? payload.iat * 1000 : null;
    const expiresAt = payload?.exp ? payload.exp * 1000 : null;
    setSessionInfo({
      issuedAt,
      expiresAt,
      remainingMs: expiresAt ? expiresAt - Date.now() : null,
    });
    warnedRef.current = false;
    expiredRef.current = false;
  }, [token]);

  useEffect(() => {
    try {
      sessionStorage.setItem("sessionToast", JSON.stringify(sessionToast));
    } catch (e) {
      // Ignore storage errors
    }
  }, [sessionToast]);

  useEffect(() => {
    if (!sessionInfo.expiresAt) return;

    const intervalId = setInterval(() => {
      const remainingMs = sessionInfo.expiresAt - Date.now();
      setSessionInfo((prev) => ({
        ...prev,
        remainingMs,
      }));

      if (remainingMs <= EXPIRY_WARNING_MS && remainingMs > 0 && !warnedRef.current) {
        warnedRef.current = true;
        setSessionToast({
          open: true,
          severity: "warning",
          message:
            `Your session is expiring in ${formatRelativeDuration(
              remainingMs
            )}. Please save any outstanding changes and sign in again.`,
        });
      }

      if (remainingMs <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        logoutWithB2C(
          `Your session needs to be refreshed. Please sign in again. (1.5)`
        );
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [logoutWithB2C, sessionInfo.expiresAt]);

  if (!initialized) return null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        noAccess,
        permissions,
        roleCapabilities,
        initialized,
        refreshToken,
        sessionInfo,
        sessionToast,
        setSessionToast,
        logoutLocal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
